// eyeq — network (сеть) economics + membership contracts.
//
// Recruiting model (Gleb, 2026-07-28): there is NO open marketing — every
// participant joins through a network. A lead earns a 20% override on their
// network's production; a person recruited by a lead earns 20% less than the
// level above them. Formally:
//
//   member at depth k (lead = 0) earns on OWN frames:  base × 0.8^k
//   member at depth k earns on EVERY frame produced anywhere strictly below
//   them in their subtree:                             base × 0.2 × 0.8^k
//
// The split telescopes to exactly `base` at any depth:
//   0.8^d + Σ_{k=0..d-1} 0.2·0.8^k = 1
// so a frame ALWAYS costs the project exactly its base rate — the network
// shape cannot blow the budget. Pinned by test/eyeq_network.test.ts.
import { z } from 'zod';

export const EYEQ_OVERRIDE_SHARE = 0.2; // each level down earns 20% less
export const EYEQ_MAX_DEPTH = 20;       // hard sanity cap for chain walks

export const EyeqRoleSchema = z.enum(['participant', 'lead', 'manager']);
export type EyeqRole = z.infer<typeof EyeqRoleSchema>;

export const EyeqMemberStatusSchema = z.enum(['invited', 'active', 'paused', 'off']);
export type EyeqMemberStatus = z.infer<typeof EyeqMemberStatusSchema>;

export const EyeqMemberSchema = z.object({
  uid: z.string().min(1),
  /** Opaque delivery-facing id (`ep_<12hex>`, ADR-0040). The Firebase uid is
   *  PII and never leaves the platform — this is what shipment tables carry. */
  public_id: z.string(),
  email: z.string(),
  role: EyeqRoleSchema,
  country: z.string(),
  locales: z.array(z.string()),
  upline_uid: z.string().nullable(),
  depth: z.number().int().min(0).max(EYEQ_MAX_DEPTH),
  status: EyeqMemberStatusSchema,
  ui_lang: z.string(),
});
export type EyeqMember = z.infer<typeof EyeqMemberSchema>;

// ── multipliers ──────────────────────────────────────────────────────────────

/** Share of the base rate a member keeps on their OWN frames. */
export function eyeqOwnMultiplier(depth: number): number {
  return Math.pow(1 - EYEQ_OVERRIDE_SHARE, depth);
}

/** Share of the base rate a member receives per frame produced ANYWHERE
 *  strictly below them (independent of how deep the worker is). */
export function eyeqOverrideMultiplier(depth: number): number {
  return EYEQ_OVERRIDE_SHARE * Math.pow(1 - EYEQ_OVERRIDE_SHARE, depth);
}

// ── exact integer split ──────────────────────────────────────────────────────

export interface EyeqSplit {
  /** cents to the worker (frame author, depth = ancestors.length) */
  worker: number;
  /** cents per ancestor, [0] = direct upline … [last] = top lead (depth 0) */
  ancestors: number[];
}

/**
 * Split `baseCents` between a worker at depth `d` and their `d` ancestors so
 * that the total is EXACTLY `baseCents` (largest-remainder rounding; ties go
 * to the worker first, then to nearer ancestors). depth 0 → worker takes all.
 */
export function eyeqSplitCents(baseCents: number, depth: number): EyeqSplit {
  if (!Number.isInteger(baseCents) || baseCents < 0) throw new Error('baseCents must be a non-negative integer');
  if (!Number.isInteger(depth) || depth < 0 || depth > EYEQ_MAX_DEPTH) throw new Error('bad depth');

  // quotas[0] = worker (depth d); quotas[1..d] = ancestors at depth d-1 … 0
  const quotas: number[] = [baseCents * eyeqOwnMultiplier(depth)];
  for (let k = depth - 1; k >= 0; k--) quotas.push(baseCents * eyeqOverrideMultiplier(k));

  const floors = quotas.map(Math.floor);
  let left = baseCents - floors.reduce((a, b) => a + b, 0);
  // hand out remaining cents by descending fractional part; stable order
  // (worker, then nearer ancestors) breaks ties deterministically
  const order = quotas
    .map((q, i) => ({ i, frac: q - Math.floor(q) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (const { i } of order) {
    if (left <= 0) break;
    floors[i] += 1;
    left -= 1;
  }
  return { worker: floors[0], ancestors: floors.slice(1) };
}

// ── invites ──────────────────────────────────────────────────────────────────

export const EyeqInviteStatusSchema = z.enum(['open', 'used', 'revoked']);

export const EyeqInviteCreateReqSchema = z.object({
  issuer_uid: z.string().min(1),
  country: z.string().min(1),
  locales: z.array(z.string().min(1)).min(1),
  email: z.string().email().optional(),
});
export type EyeqInviteCreateReq = z.infer<typeof EyeqInviteCreateReqSchema>;

export const EyeqInviteCreateResSchema = z.object({
  code: z.string(),
  issuer_uid: z.string(),
  country: z.string(),
  locales: z.array(z.string()),
});
export type EyeqInviteCreateRes = z.infer<typeof EyeqInviteCreateResSchema>;

export const EyeqJoinReqSchema = z.object({
  code: z.string().min(1),
  uid: z.string().min(1),
  email: z.string().email(),
  ui_lang: z.string().default('en'),
});
export type EyeqJoinReq = z.infer<typeof EyeqJoinReqSchema>;

export const EyeqJoinResSchema = z.object({
  member: EyeqMemberSchema,
  own_multiplier: z.number(),
  override_multiplier: z.number(),
});
export type EyeqJoinRes = z.infer<typeof EyeqJoinResSchema>;

// ── team view ────────────────────────────────────────────────────────────────

export const EyeqTeamMemberSchema = EyeqMemberSchema.extend({
  frames_total: z.number().int(),
  frames_week: z.number().int(),
  earned_cents: z.number().int(),
  last_upload_at: z.string().nullable(),
});
export const EyeqTeamResSchema = z.object({
  uid: z.string(),
  team: z.array(EyeqTeamMemberSchema),
  override_cents_total: z.number().int(),
});
export type EyeqTeamRes = z.infer<typeof EyeqTeamResSchema>;

// ── credit (called by the eval/QA pipeline when a frame is accepted) ─────────

export const EyeqCreditReqSchema = z.object({
  asset_id: z.string().min(1),
  worker_uid: z.string().min(1),
  base_cents: z.number().int().min(0),
});
export type EyeqCreditReq = z.infer<typeof EyeqCreditReqSchema>;

export const EyeqCreditResSchema = z.object({
  asset_id: z.string(),
  total_cents: z.number().int(),
  rows: z.array(z.object({
    uid: z.string(),
    kind: z.enum(['own', 'override']),
    cents: z.number().int(),
    gap: z.number().int(), // levels between receiver and worker (0 = own)
  })),
});
export type EyeqCreditRes = z.infer<typeof EyeqCreditResSchema>;
